import React, { useState } from 'react';
import { X } from 'lucide-react';
import StorefrontFrame from '../storefront/frame/StorefrontFrame';
import { Photo, OrderNowButton } from '../storefront/primitives';
import { Gallery } from '../storefront/sections/Story';
import { sizesFor } from '../storefront/data/media';

/**
 * StorefrontAboutPage (v4.25, reframed v5.67) — the dedicated "Our story"
 * page linked from the storefront nav. It wears the v7 frame (the home
 * page's header, phone menu, footer, theme, fonts and fail-closed gate)
 * and renders the merchant's own long-form story: the About copy, the
 * story photo and the gallery. Nothing here is typed per tenant.
 */
const AboutBody = ({ brand: b, theme, loading, show, goHome }) => {
  const [lightbox, setLightbox] = useState(null);
  const brandName = b.brand_name || 'Our Kitchen';
  const headline = b.about_headline || b.story_headline || 'Our story';
  // the story headline carries its accent phrase on the home page; the About
  // copy has its own headline and stands alone
  const emphasis = b.about_headline ? '' : (b.story_headline ? (b.story_headline_italic || '') : '');
  const bodyHtml = b.about_body_html || b.story_body_html || b.about_html || null;
  const storyImage = b.story_image_url || b.hero_image_url || null;
  const gallery = Array.isArray(b.gallery_items) ? b.gallery_items.filter((g) => g && g.image_url) : [];
  const ctaLabel = show.menu ? 'Explore the menu' : show.plans ? 'Explore our plans' : 'Back to store';
  const ctaTarget = show.menu ? 'menu' : show.plans ? 'plans' : undefined;

  return (
    <div data-testid="storefront-about-page">
      <section className="s6-section" data-testid="about-story">
        <div className="s6-container">
          <div className="max-w-3xl mx-auto">
            {b.story_eyebrow && <span className="s6-eyebrow">{b.story_eyebrow}</span>}
            <h1 className="s6-h2">
              {headline}{emphasis ? ' ' : ''}
              {emphasis && <span className={theme.accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'}>{emphasis}</span>}
            </h1>

            {storyImage && (
              <Photo src={storyImage} alt={brandName} ratio="16 / 9" eager sizes={sizesFor('tile')} className="mt-8" />
            )}

            {loading && !bodyHtml ? (
              <div className="s6-skeleton h-40 mt-8" aria-hidden="true" />
            ) : bodyHtml ? (
              <div
                className="s6-prose mt-8"
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
                data-testid="about-body"
              />
            ) : (
              <div className="s6-prose mt-8" data-testid="about-body">
                <p>
                  {brandName} is a kitchen built on a simple idea: great food you can count on, from
                  people who cook it like it's for their own family.
                </p>
                <p>
                  We're still writing our story here — in the meantime, come say hello, grab a meal
                  pass, or reach out any time.
                </p>
              </div>
            )}

            <div className="mt-10">
              <OrderNowButton onClick={() => goHome(ctaTarget)} data-testid="about-cta">{ctaLabel}</OrderNowButton>
            </div>
          </div>
        </div>
      </section>

      {/* the merchant's own photos, as on the home page; nothing renders when there are none */}
      <Gallery items={gallery} theme={theme} copy={{}} onOpen={setLightbox} />

      {lightbox !== null && gallery[lightbox] && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" role="dialog" aria-modal="true" onClick={() => setLightbox(null)} data-testid="about-gallery-lightbox">
          <button type="button" onClick={() => setLightbox(null)} aria-label="Close photo" className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"><X className="h-5 w-5" /></button>
          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={gallery[lightbox].image_url} alt={gallery[lightbox].caption || ''} className="w-full max-h-[80vh] object-contain rounded-xl" />
            {gallery[lightbox].caption && <figcaption className="mt-3 text-center text-white/80 text-sm">{gallery[lightbox].caption}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
};

const StorefrontAboutPage = () => (
  <StorefrontFrame pageType="about">
    {(ctx) => <AboutBody {...ctx} />}
  </StorefrontFrame>
);

export default StorefrontAboutPage;
